import type { SearchConversationEnriched } from 'lib-search-types';
import { getAsyncStatusFromRtkQueryStates } from 'lib-shared-auth';
import { AsyncStatus } from 'lib-shared-types-api';
import { useCallback, useEffect } from 'react';

import { useLazyReadSearchInfiniteQuery } from 'apis/search';
import { transformWebSocketConversationToSearchConversation } from 'app/views/layout/views/search/utils/useSearchConversationEventsEffect/utils/transformWebSocketConversationToSearchConversation';
import { useConversationEventsEffect } from 'utils/conversation/useConversationEventsEffect';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import { conversationMatchesSearchParams } from '../conversationMatchesSearchParams/conversationMatchesSearchParams';
import { useConversationsParamsToApiArg } from '../useConversationsParamsToApiArg/useConversationsParamsToApiArg';
import { defaultConversationsPageSize } from '../useConversationsParamsToApiArg/utils/transformConversationsParamsToSearchApiArg';

type UseConversationsResult = {
  /** The async status of the search conversations endpoint. */
  readonly asyncStatus: AsyncStatus;
  /** The conversations on the current page. */
  readonly conversations: readonly SearchConversationEnriched[];
  /** Whether there is a next page to navigate to. */
  readonly hasNextPage: boolean;
  /** Whether there is a previous page to navigate to. */
  readonly hasPreviousPage: boolean;
  /** The method for navigating to the next page. */
  readonly nextPage: () => void;
  /** The method for navigating to the previous page. */
  readonly previousPage: () => void;
};

/**
 * A hook that handles fetching, updating conversations on the conversations
 * overview page, including reacting to real-time updates via WebSocket events.
 */
export const useConversations = (): UseConversationsResult => {
  const { searchApiArg } = useConversationsParamsToApiArg();
  const { params, setParam } = useAppSearchParams('/conversations');

  const [
    readSearchConversations,
    {
      results,
      isUninitialized,
      isFetching,
      isSuccess,
      isError,
      hasNextPage,
      hasPreviousPage,
      updateResults,
    },
  ] = useLazyReadSearchInfiniteQuery<'conversations', true>({
    infinite: false,
  });

  // Trigger search whenever searchApiArg changes.
  useEffect(() => {
    readSearchConversations(searchApiArg);
  }, [searchApiArg, readSearchConversations]);

  const pageSize = searchApiArg.pageSize ?? defaultConversationsPageSize;
  const currentPage = searchApiArg.page ?? 0;

  const handleNextPage = useCallback(() => {
    const newOffset = (currentPage + 1) * pageSize;
    setParam('offset', newOffset.toString());
  }, [currentPage, pageSize, setParam]);

  const handlePreviousPage = useCallback(() => {
    const newOffset = (currentPage - 1) * pageSize;
    setParam('offset', newOffset > 0 ? newOffset.toString() : undefined);
  }, [currentPage, pageSize, setParam]);

  // Handle real-time WebSocket updates.
  useConversationEventsEffect(({ params: webSocketParams }) => {
    const isFirstPage = !hasPreviousPage;
    const { conversation: wsConversation } = webSocketParams;
    const isIncomingConversationMatchesParams = conversationMatchesSearchParams(
      wsConversation,
      params,
    );
    const isOnCurrentPage = results.some((c) => c.id === wsConversation.id);
    const sortField = searchApiArg.sortBy?.field;
    const isSortDescending = searchApiArg.sortBy?.order
      ? searchApiArg.sortBy.order === 'desc'
      : true;

    if (isOnCurrentPage) {
      if (isIncomingConversationMatchesParams) {
        // Update the existing conversation in place.
        const transformedConversation =
          transformWebSocketConversationToSearchConversation(webSocketParams);

        updateResults((draft) => {
          const idx = draft.findIndex(({ id }) => id === wsConversation.id);

          if (idx !== -1) {
            draft[idx] = transformedConversation;
          }

          // Re-sort if a sort field is active.
          if (sortField) {
            draft.sort((a, b) => {
              const aVal = a[sortField] ?? '';
              const bVal = b[sortField] ?? '';
              if (aVal < bVal) return isSortDescending ? 1 : -1;
              if (aVal > bVal) return isSortDescending ? -1 : 1;
              return 0;
            });
          }
        });
      } else {
        // Remove the conversation if it no longer matches the filters.
        updateResults((draft) => {
          const idx = draft.findIndex(({ id }) => id === wsConversation.id);
          if (idx !== -1) draft.splice(idx, 1);
        });
      }
    } else if (isFirstPage && isIncomingConversationMatchesParams) {
      // Prepend the new conversation on the first page.
      const transformedConversation =
        transformWebSocketConversationToSearchConversation(webSocketParams);

      updateResults((draft) => {
        draft.unshift(transformedConversation);
      });
    }
  });

  const asyncStatus = getAsyncStatusFromRtkQueryStates({
    isUninitialized,
    isFetching,
    isSuccess,
    isError,
  });

  return {
    asyncStatus,
    conversations: results,
    hasNextPage,
    hasPreviousPage,
    nextPage: handleNextPage,
    previousPage: handlePreviousPage,
  };
};
